// ─────────────────────────────────────────────────────────────
// SUPABASE CLIENT
// Token + refresh-token logic, plus a tiny REST wrapper.
//
// Reads config from .env (Create React App style):
//   REACT_APP_SUPABASE_URL=https://...
//   REACT_APP_SUPABASE_ANON_KEY=...
// ─────────────────────────────────────────────────────────────

export const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
export const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Fail loudly during development if .env is missing.
  console.error(
    "Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY. " +
    "Add them to a .env file at the project root and restart `npm start`."
  );
}

let _token = localStorage.getItem("fos_token") || null;
let _refreshToken = localStorage.getItem("fos_refresh_token") || null;
let _tokenExpiry = localStorage.getItem("fos_token_expiry")
  ? parseInt(localStorage.getItem("fos_token_expiry"))
  : null;

// In-flight refresh promise so concurrent authFetch calls share one refresh.
let _refreshInFlight = null;

export const authHeaders = () => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${_token || SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

// Internal: write token state to memory + localStorage.
// Inlined here so ensureValidToken doesn't depend on `sb` being initialized.
function _persistToken(token, refreshToken, expiresIn) {
  _token = token;
  _refreshToken = refreshToken || localStorage.getItem("fos_refresh_token");
  _tokenExpiry = Math.floor(Date.now() / 1000) + (expiresIn || 3600);
  if (token) {
    localStorage.setItem("fos_token", token);
    if (refreshToken) localStorage.setItem("fos_refresh_token", refreshToken);
    localStorage.setItem("fos_token_expiry", _tokenExpiry.toString());
  } else {
    localStorage.removeItem("fos_token");
    localStorage.removeItem("fos_refresh_token");
    localStorage.removeItem("fos_token_expiry");
  }
}

export const ensureValidToken = async () => {
  // No token at all — nothing to refresh, anon key will be used.
  if (!_token) return true;

  // No expiry recorded — we don't know if the token is fresh.
  // Treat as valid; if it's actually expired, the REST call will 401
  // and the caller can decide what to do. (Better than silently
  // refreshing a token we have no metadata for.)
  if (!_tokenExpiry) return true;

  const now = Date.now() / 1000;
  if (now <= (_tokenExpiry - 60)) return true; // still fresh

  // Needs refresh.
  if (!_refreshToken) {
    console.warn("[supabase] token expired and no refresh token available");
    return false;
  }

  // Coalesce concurrent refreshes.
  if (_refreshInFlight) return _refreshInFlight;

  _refreshInFlight = (async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: _refreshToken }),
      });
      const data = await res.json();
      if (data.access_token) {
        _persistToken(data.access_token, data.refresh_token, data.expires_in);
        console.log("✓ Token refreshed successfully");
        return true;
      }
      console.error("[supabase] token refresh response missing access_token:", data);
      return false;
    } catch (e) {
      console.error("[supabase] token refresh failed:", e);
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();

  return _refreshInFlight;
};

// ─────────────────────────────────────────────────────────────
// authFetch — wraps ensureValidToken + auth headers + fetch.
//
// Use this for ALL Supabase REST calls instead of raw fetch. It
// guarantees the JWT is refreshed before each request, fixing the
// "lost connection after ~1 hour" bug.
//
// Example:
//   const res = await authFetch(`${SUPABASE_URL}/rest/v1/trades`);
//   const data = await res.json();
//
// You can still pass method/body/etc as the second argument:
//   await authFetch(url, { method: "POST", body: JSON.stringify(data) });
// ─────────────────────────────────────────────────────────────
export async function authFetch(url, options = {}) {
  await ensureValidToken();
  const headers = { ...authHeaders(), ...(options.headers || {}) };
  return fetch(url, { ...options, headers });
}

export const sb = {
  setToken(token, refreshToken = null, expiresIn = 3600) {
    _persistToken(token, refreshToken, expiresIn);
  },
  async query(table, opts = {}) {
    const params = new URLSearchParams();
    if (opts.select) params.set("select", opts.select);
    const res = await authFetch(
      `${SUPABASE_URL}/rest/v1/${table}?${params.toString()}`
    );
    return res.json();
  },
  async insert(table, data) {
    const res = await authFetch(`${SUPABASE_URL}/rest/v1/${table}`, {
      method: "POST",
      headers: { Prefer: "return=representation" },
      body: JSON.stringify(data),
    });
    return res.json();
  },
  async signOut() {
    _persistToken(null, null, 0);
  },
};
